<template>
  <div class="container-xxl get-started-page">
    <HeroSection />
    <FormatComparisonSection />
    <PoweredByEddySection />

    <!-- Third Section - 3 Simple Steps -->
    <section
      class="section steps-section"
      id="3-simple-steps"
      aria-labelledby="steps-heading"
    >
      <div class="section-container steps-container">
        <!-- Header Section -->
        <div class="section-header steps-header">
          <div
            class="laptop-icon"
            v-html="LAPTOP_ICON_SVG"
            aria-hidden="true"
          ></div>
          <h2 id="steps-heading" class="text-title-large steps-title">
            From Idea to Classroom — in 3 Simple Steps
          </h2>
        </div>

        <!-- Steps Cards -->
        <div class="card-grid-3 steps-cards">
          <div
            v-for="(step, index) in steps"
            :key="index"
            :class="['card', 'step-card', `step-${index + 1}`]"
          >
            <div class="step-content">
              <div class="step-image" aria-hidden="true">
                <img
                  :src="step.icon"
                  :alt="`${step.title} illustration`"
                  class="step-icon"
                />
              </div>
              <div class="step-text">
                <h3 class="step-title">
                  Step {{ index + 1 }}: {{ step.title }}
                </h3>
                <p class="text-body-medium step-description">
                  {{ step.description }}
                </p>
              </div>
            </div>
            <ActionButton
              v-if="step.buttonText"
              variant="primary"
              size="large"
              full-width
              customClass="step-button"
              @click="step.action"
            >
              {{ step.buttonText }}
              <template #icon-right>
                <ArrowShortRightIcon
                  v-if="step.buttonIcon === 'arrow'"
                  color="currentColor"
                />
                <DocumentTextIcon
                  v-else-if="step.buttonIcon === 'document'"
                  color="currentColor"
                />
              </template>
            </ActionButton>
          </div>
        </div>
      </div>
    </section>

    <!-- Fourth Section - See Makercart in Action -->
    <section class="section video-section" aria-labelledby="video-heading">
      <div class="section-container video-container">
        <!-- Header Section -->
        <div class="section-header video-header">
          <div
            class="laptop-icon"
            v-html="LAPTOP_ICON_SVG"
            aria-hidden="true"
          ></div>
          <div class="section-content video-content">
            <h2 id="video-heading" class="text-title-large video-title">
              See Makercart in Action
            </h2>
            <p class="text-body-small text-subtitle">
              Take a closer look at the mobile makerspace, its organized
              materials and how it supports hands-on learning in the classroom.
            </p>
          </div>
        </div>

        <!-- Video Player -->
        <div class="video-player">
          <div class="youtube-container">
            <div class="youtube-wrapper">
              <iframe
                class="youtube-iframe"
                src="https://www.youtube.com/embed/EI7kMB8Thjg?rel=0&modestbranding=1&showinfo=0&controls=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0"
                title="See Makercart in Action"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="video-actions">
          <ActionButton
            variant="primary"
            size="large"
            @click="
              goToLink(
                '/makercart/catalog?tab=custom&sort=top-activities',
                '_blank'
              )
            "
          >
            Explore Makercart
            <template #icon-right>
              <ArrowShortRightIcon color="currentColor" />
            </template>
          </ActionButton>
        </div>
      </div>
    </section>

    <ActivityPostersSection />

    <!-- Fifth Section - What Educators Say About Learning with edm8ker -->
    <section
      class="section testimonials-section"
      aria-labelledby="testimonials-heading"
    >
      <div class="section-container testimonials-container">
        <!-- Header Section -->
        <div class="section-header testimonials-header">
          <div
            class="laptop-icon"
            v-html="LAPTOP_ICON_SVG"
            aria-hidden="true"
          ></div>
          <div class="section-content testimonials-content">
            <h2
              id="testimonials-heading"
              class="text-title-large testimonials-title"
            >
              What Educators Say About Learning with edm8ker
            </h2>
            <p class="text-body-small text-subtitle">
              Straight from classrooms and afterschool programs — here's what
              they had to say:
            </p>
          </div>
        </div>

        <TestimonialsCarousel />
      </div>
    </section>

    <!-- Sixth Section - Ordering Your Makercart Is Simple -->
    <section class="section demo-section" aria-labelledby="demo-heading">
      <div class="section-container demo-container">
        <!-- Header Section -->
        <div class="section-header demo-header">
          <div
            class="laptop-icon"
            v-html="LAPTOP_ICON_SVG"
            aria-hidden="true"
          ></div>
          <div class="section-content demo-content">
            <h2 id="demo-heading" class="text-title-large demo-title">
              Ordering Your Makercart Is Simple
            </h2>
            <p class="text-body-small text-subtitle">
              Browse activities, select the setup that works for your classroom
              and submit your request. Our team will follow up to confirm the
              configuration and next steps.
            </p>
          </div>
        </div>

        <!-- Demo Video -->
        <div class="demo-video">
          <div class="video-container-local">
            <div class="video-wrapper-local">
              <video
                ref="demoVideo"
                class="video-player-local"
                controls
                controlsList="nodownload"
                muted
                preload="metadata"
                aria-label="Tutorial: Placing your Makercart order (Silent Video) — browse activities, build a cart, and request a quote"
                @click="togglePlayPause"
              >
                <source
                  src="/videos/eddy-placing-order-tutorial.mp4"
                  type="video/mp4"
                />
                <track
                  kind="descriptions"
                  src="/videos/eddy-placing-order-tutorial-desc.vtt"
                  srclang="en"
                  label="English Descriptions"
                  default
                />
                Your browser does not support the video tag.
              </video>
              <button
                class="video-overlay"
                v-if="!isDemoVideoPlaying"
                @click="playDemoVideo"
                aria-label="Play tutorial video: Placing your Makercart order"
                type="button"
              >
                <div class="play-button" aria-hidden="true">
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 80 80"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <circle
                      cx="40"
                      cy="40"
                      r="40"
                      fill="rgba(255, 255, 255, 0.95)"
                    />
                    <path d="M32 26L56 40L32 54V26Z" fill="#003D6A" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Start Your Makercart Order CTA -->
        <div class="demo-cta">
          <ActionButton
            variant="primary"
            size="large"
            type="button"
            @click="router.push('/makercart/catalog')"
          >
            Start Your Makercart Order
            <template #icon-right>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 3L3.04936 3.20987C3.91136 3.38227 4.55973 4.09732 4.6472 4.97203L4.8 6.5M4.8 6.5L5.7886 14.7383C5.90922 15.7435 6.76195 16.5 7.77435 16.5H16.7673C18.3733 16.5 19.7733 15.407 20.1628 13.8489L21.2855 9.35783C21.6485 7.90619 20.5505 6.5 19.0542 6.5H4.8Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M13 13.5H9"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <circle cx="8.5" cy="20" r="1.5" fill="white" />
                <circle cx="17.5" cy="20" r="1.5" fill="white" />
              </svg>
            </template>
          </ActionButton>
        </div>

        <StartingPointSection />
      </div>
    </section>

    <PlanningFundingSection />
  </div>
  <ChatbotAssistant page-name="Get Started page" />
</template>

<script setup>
import ChatbotAssistant from "@/components/maker-cart/ChatbotAssistant.vue";
import HeroSection from "@/components/maker-cart/get-started/HeroSection.vue";
import FormatComparisonSection from "@/components/maker-cart/get-started/FormatComparisonSection.vue";
import PoweredByEddySection from "@/components/maker-cart/get-started/PoweredByEddySection.vue";
import ActivityPostersSection from "@/components/maker-cart/get-started/ActivityPostersSection.vue";
import StartingPointSection from "@/components/maker-cart/get-started/StartingPointSection.vue";
import PlanningFundingSection from "@/components/maker-cart/get-started/PlanningFundingSection.vue";
import TestimonialsCarousel from "@/components/maker-cart/get-started/TestimonialsCarousel.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { ArrowShortRightIcon, DocumentTextIcon } from "@/components/icons";
import { useRouter } from "vue-router";
import { ref, onMounted } from "vue";

const router = useRouter();
const demoVideo = ref(null);
const isDemoVideoPlaying = ref(false);

const LAPTOP_ICON_SVG = `<svg width="68" height="51" viewBox="0 0 68 51" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0.666687 44C0.666687 42.159 2.15907 40.6666 4.00002 40.6666H64C65.841 40.6666 67.3334 42.159 67.3334 44V47.3333C67.3334 49.1742 65.841 50.6666 64 50.6666H4.00002C2.15907 50.6666 0.666687 49.1742 0.666687 47.3333V44Z" fill="#E00069"/>
  <path d="M64 40.6666V40.9049C63.9999 42.6142 62.5077 43.9995 60.667 43.9996H7.33301C5.49228 43.9995 4.00011 42.6142 4 40.9049V40.6666H64ZM20.5664 0.666626C22.133 0.666626 23.4358 1.91968 24.6777 2.87463C25.3986 3.42885 26.324 3.76226 27.333 3.76233H40.667C41.676 3.76226 42.6014 3.42885 43.3223 2.87463C44.5642 1.91968 45.867 0.666626 47.4336 0.666626H60.667C62.5078 0.66679 64 2.05298 64 3.76233V35.6666H4V3.76233C4 2.05298 5.49221 0.666789 7.33301 0.666626H20.5664Z" fill="#E00069"/>
</svg>`;

const goToLink = (link, target) => {
  if (target === "_blank") {
    window.open(link, target);
  } else {
    location.href = link;
  }
};

const steps = [
  {
    title: "Pick Your Format",
    icon: "/images/maker-cart/get-started/step-1-pick-format.svg",
    description:
      "Select a compact Makercrate for single units, a Signature Makercart for curated themes, or a Custom Makercart built to your specs.",
  },
  {
    title: "Preview Activities on Eddy",
    icon: "/images/maker-cart/get-started/step-2-preview.svg",
    description:
      "Explore standards-aligned lesson slides, student worksheets, and material lists on Eddy.",
    buttonText: "Browse through Lessons",
    buttonIcon: "arrow",
    action: () => router.push("/makercart/catalog?tab=makercrate"),
  },
  {
    title: "Confirm Your Order",
    icon: "/images/maker-cart/get-started/step-3-confirm.svg",
    description:
      "Purchase pre-configured crates and carts instantly, or submit your custom cart configuration for a district quote.",
    buttonText: "Request a Quote",
    buttonIcon: "document",
    action: () => goToLink("mailto:hello@edm8ker.com"),
  },
];

const playDemoVideo = () => {
  if (demoVideo.value) {
    demoVideo.value.play();
    isDemoVideoPlaying.value = true;
  }
};

const togglePlayPause = () => {
  if (demoVideo.value) {
    if (demoVideo.value.paused) {
      demoVideo.value.play();
      isDemoVideoPlaying.value = true;
    } else {
      demoVideo.value.pause();
      isDemoVideoPlaying.value = false;
    }
  }
};

onMounted(() => {
  if (demoVideo.value) {
    demoVideo.value.addEventListener("play", () => {
      isDemoVideoPlaying.value = true;
    });
    demoVideo.value.addEventListener("pause", () => {
      isDemoVideoPlaying.value = false;
    });
    demoVideo.value.addEventListener("ended", () => {
      isDemoVideoPlaying.value = false;
    });
  }
});
</script>

<style scoped>
/* Base Styles */
a {
  text-decoration: none;
}

/* Skip to main content link (visually hidden until focused) */
.skip-link {
  position: absolute;
  top: -100%;
  left: 0;
  background: var(--primary-blue, #003d6a);
  color: #ffffff;
  padding: 12px 24px;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  text-decoration: none;
  z-index: 9999;
  border-radius: 0 0 8px 0;
}

.skip-link:focus {
  top: 0;
  outline: 3px solid #e00069;
  outline-offset: 2px;
}

/* Typography Base Classes */
.text-title-large {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 36px;
  line-height: 52px;
  color: var(--primary-blue-dark, #061f32);
  margin: 0;
  flex: 1;
}

.text-title-medium {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: var(--primary-blue-dark, #061f32);
  margin: 0;
}

.text-body-large {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--neutral-700, #6c737f);
  flex: 1;
  margin: 0;
  text-align: right;
}

.text-body-medium {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: #3b4648;
  margin: 0;
}

.text-body-small {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--black-body-text, #3b4648);
  margin: 0;
}

/* Layout Base Classes */
.section {
  background-color: #ffffff;
  padding: 80px 0px 50px;
}

.section-container {
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
}

.demo-cta {
  display: flex;
  justify-content: center;
}

.section-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0px;
  border-radius: 20px;
}

.section-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.section-title {
  flex: 0 0 628px;
}

.section-description {
  flex: 0 0 628px;
  text-align: right;
}

.laptop-icon {
  display: flex;
  justify-content: left;
  width: 100%;
}

/* Card Base Classes */
.card {
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.card-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px;
}

.card-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;
}

/* Button Base Classes */
.video-overlay:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 3px;
}

/* Page Layout */
.get-started-page {
  border-top: 1px solid #d8dae5;
  padding: 0px 150px;
}

/* Responsive Design */
@media (max-width: 1440px) {
  .get-started-page {
    padding: 0px 80px;
  }
}

@media (max-width: 1200px) {
  .get-started-page {
    padding: 0px 40px;
  }
  .section {
    padding: 60px 0px 40px;
  }

  .section-header {
    padding: 20px 0px;
  }

  .section-content {
    flex-direction: column;
    gap: 32px;
  }

  .section-title,
  .section-description {
    flex: none;
    width: 100%;
  }

  .section-description {
    text-align: left;
  }

  .text-body-large {
    text-align: left;
  }

  .text-title-large {
    font-size: 38px;
    line-height: 44px;
  }
}

@media (max-width: 992px) {
  .get-started-page {
    padding: 0px 20px;
  }

  .section-header {
    padding: 16px 0px;
  }

  .text-title-large {
    font-size: 32px;
    line-height: 38px;
  }

  .text-title-medium {
    font-size: var(--fs-xl);
    line-height: 30px;
  }

  .text-body-large {
    font-size: var(--fs-big);
    line-height: 26px;
  }
}

@media (max-width: 768px) {
  .get-started-page {
    padding: 0px 20px;
  }

  .section {
    padding: 40px 0px 30px;
  }

  .laptop-icon {
    height: 60px;
  }

  .laptop-icon svg {
    width: 60px;
    height: 45px;
  }

  .text-title-large {
    font-size: var(--fs-xxl);
    line-height: 34px;
  }

  .text-title-medium {
    font-size: var(--fs-large);
    line-height: 28px;
  }

  .text-body-large {
    font-size: 18px;
    line-height: 24px;
  }
}

@media (max-width: 480px) {
  .get-started-page {
    padding: 0px 16px;
  }
  .section {
    padding: 30px 0px 20px;
  }

  .section-container {
    gap: 20px;
  }
  .section-header {
    gap: 16px;
    padding: 10px 0px;
  }

  .section-content {
    gap: 24px;
  }

  .laptop-icon {
    height: 48px;
  }

  .laptop-icon svg {
    width: 48px;
    height: 36px;
  }

  .text-title-large {
    font-size: var(--fs-large);
    line-height: 30px;
  }

  .text-title-medium {
    font-size: var(--fs-big);
    line-height: 26px;
  }
  .text-body-large {
    font-size: var(--fs-medium);
    line-height: 22px;
  }
}

.steps-header .laptop-icon {
  display: flex;
  justify-content: left;
  width: 100%;
}

.steps-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;
}

.step-card {
  width: 100%;
  min-height: 545px;
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  background-color: var(--white-hover-1);
}

.step-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.step-image {
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
}

.step-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.step-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-title {
  font-weight: 700;
  font-size: 36px;
  line-height: 44px;
  margin: 0;
  color: var(--pink-dark-1);
}

.step-description {
  font-family: var(--font-family-primary);
  font-weight: 400;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--black-body-text);
  margin: 0;
}

.step-button {
  background-color: var(--primary-blue, #003d6a);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  min-height: 56px;
}

.step-button:hover {
  background-color: var(--primary-blue-dark, #00253b);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.2);
}

/* Steps Section Responsive */
@media (max-width: 1200px) {
  .steps-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
  }

  .step-title {
    font-size: 30px !important;
    line-height: 36px !important;
  }

  .step-description {
    font-size: var(--fs-big);
    line-height: 26px;
  }
}

@media (max-width: 992px) {
  .steps-cards {
    flex-direction: column;
    gap: 32px;
    align-items: center;
  }

  .step-card {
    width: 100%;
    max-width: 500px;
    height: auto;
    min-height: 500px;
  }

  .step-title {
    font-size: var(--fs-xxl) !important;
    line-height: 34px !important;
  }

  .step-description {
    font-size: 18px;
    line-height: 24px;
  }
}

@media (max-width: 768px) {
  .step-card {
    height: auto;
    min-height: 450px;
    padding: 20px;
  }

  .text-body-small {
    font-size: var(--fs-medium);
  }

  .step-image {
    width: 120px;
    height: 120px;
  }

  .step-title {
    font-size: var(--fs-xl) !important;
    line-height: 30px !important;
  }

  .step-description {
    font-size: var(--fs-medium);
    line-height: 22px;
  }

  .step-button {
    padding: 12px 20px;
    font-size: var(--fs-small);
  }
}

@media (max-width: 480px) {
  .step-card {
    min-height: 400px;
    padding: 16px;
  }

  .step-content {
    gap: 12px;
  }

  .step-image {
    width: 135px;
    height: 135px;
  }

  .step-text {
    gap: 8px;
  }

  .step-title {
    font-size: var(--fs-large) !important;
    line-height: 26px !important;
  }

  .step-description {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .step-button {
    padding: 10px 16px;
    font-size: 13px;
    min-height: 48px;
  }
}

.video-header .laptop-icon {
  display: flex;
  justify-content: left;
  width: 100%;
}

.video-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

/* Local Video Container Styles */
.video-container-local {
  position: relative;
  width: 100%;
  height: 761px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #f8f9fb 0%, #e8eaed 100%);
  border: 2px solid rgba(224, 0, 105, 0.1);
}

.video-wrapper-local {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 22px;
  overflow: hidden;
  background: white;
  color: black;
}

.video-player-local {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 22px;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.15) 0%,
    rgba(224, 0, 105, 0.15) 100%
  );
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
}

.video-overlay:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.25) 0%,
    rgba(224, 0, 105, 0.25) 100%
  );
}

.video-overlay:hover .play-button svg {
  transform: scale(1.1);
}

.play-button {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.play-button svg {
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
}

/* YouTube Container Styles (keeping for the other video section) */
.youtube-container {
  position: relative;
  width: 100%;
  height: 761px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #f8f9fb 0%, #e8eaed 100%);
  border: 2px solid rgba(224, 0, 105, 0.1);
}

.youtube-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 22px;
  overflow: hidden;
}

.youtube-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 22px;
}

/* Branding Overlay */
.video-overlay-branding {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
}

.branding-badge {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(224, 0, 105, 0.1);
  transition: all 0.3s ease;
}

.branding-badge:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.brand-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 600;
  font-size: var(--fs-small);
  color: #061f32;
  letter-spacing: 0.5px;
}

.brand-icon {
  color: #e00069;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

.testimonials-header {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-direction: column;
  padding: 24px 0px;
}

.testimonials-header .laptop-icon {
  display: flex;
  justify-content: left;
  width: 100%;
}

.testimonials-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.testimonials-action {
  display: flex;
  justify-content: center;
  padding: 0 80px;
}

.demo-header {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-direction: column;
  padding: 24px 0px;
}

.demo-header .laptop-icon {
  display: flex;
  justify-content: left;
  width: 100%;
}

.demo-action-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  width: 100%;
}

.demo-action {
  display: flex;
  justify-content: center;
  padding: 0 80px;
}

.text-subtitle {
  max-width: 50%;
  text-align: right;
}

@media (max-width: 1440px) {
  .testimonial-image {
    width: 180px;
  }

  .testimonial-image.victor,
  .testimonial-image.matthew {
    width: 130px;
  }
}

@media (max-width: 1200px) {
  .video-player,
  .demo-video {
    padding: 0;
  }

  .youtube-container,
  .video-container-local {
    height: 600px;
  }
}

@media (max-width: 992px) {
  .video-player,
  .demo-video {
    padding: 0;
  }

  .youtube-container,
  .video-container-local {
    height: 500px;
  }

  .play-button svg {
    width: 64px;
    height: 64px;
  }

  .branding-badge {
    padding: 6px 12px;
  }

  .brand-text {
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .steps-cards {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .youtube-container,
  .video-container-local {
    height: 400px;
  }

  .play-button svg {
    width: 56px;
    height: 56px;
  }

  .video-overlay-branding {
    top: 16px;
    right: 16px;
  }

  .branding-badge {
    padding: 6px 10px;
  }

  .brand-text {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .video-player,
  .demo-video {
    padding: 0;
  }

  .youtube-container,
  .video-container-local {
    height: 300px;
  }

  .play-button svg {
    width: 48px;
    height: 48px;
  }

  .video-overlay-branding {
    top: 12px;
    right: 12px;
  }

  .branding-badge {
    padding: 4px 8px;
  }

  .brand-text {
    font-size: 10px;
  }

  .brand-icon svg {
    width: 16px;
    height: 16px;
  }

  .text-subtitle {
    max-width: 100%;
    text-align: left;
  }
}
</style>
